import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useCart } from "@/lib/cart";
import { useCartTotals } from "@/lib/cart-totals";
import { useFormatMoney } from "@/lib/currency";
import { listShippingMethods, type ShippingMethod } from "@/lib/products";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { ArrowLeft, CheckCircle2, Truck, Package, AlertCircle, Zap } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { createNotification } from "@/lib/notifications";
import { motion } from "framer-motion";
import { HummingbirdLoader } from "@/components/HummingbirdLoader";

export const Route = createFileRoute("/app/customer/checkout")({ component: CheckoutPage });

type Step = "review" | "shipping" | "confirm";

function CheckoutPage() {
  const { items, clearCart } = useCart();
  const { profile } = useAuth();
  const nav = useNavigate();
  const fmt = useFormatMoney();
  const [step, setStep] = useState<Step>("review");
  const [shippingMethodId, setShippingMethodId] = useState<string>("");
  const [shipPreference, setShipPreference] = useState<"complete" | "part">("complete");
  const [orderNotes, setOrderNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmedGangSheets, setConfirmedGangSheets] = useState(false);

  const { data: shippingMethods = [] } = useQuery({
    queryKey: ["shipping-methods-active"],
    queryFn: listShippingMethods,
  });

  const activeShipping = shippingMethods.filter((s) => s.active);
  const selectedShipping = activeShipping.find((s) => s.id === shippingMethodId);
  const shippingCost = selectedShipping ? Number(selectedShipping.price) : 0;
  const totals = useCartTotals(items, shippingCost);

  // Block: empty cart
  if (items.length === 0 && !submitting) {
    return (
      <div className="rounded-3xl border border-border bg-card p-12 text-center shadow-soft">
        <Package className="mx-auto h-12 w-12 text-muted-foreground/40 mb-4" />
        <p className="font-medium">Nothing to checkout</p>
        <p className="mt-1 text-sm text-muted-foreground">Your cart is empty.</p>
        <Link to="/app/customer/shop">
          <Button className="mt-4 rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90">
            Browse shop
          </Button>
        </Link>
      </div>
    );
  }

  // Block: any gang sheet item without a saved gang sheet design
  const pendingGangSheetItems = items.filter((i) => i.requiresGangSheet && !i.gangSheetLayoutId);

  const handlePlaceOrder = async () => {
    if (!profile?.customer_id) {
      toast.error("Customer profile missing — please contact support.");
      return;
    }
    if (pendingGangSheetItems.length > 0) {
      toast.error("Save a gang sheet design for every gang sheet item before placing your order.");
      setStep("review");
      return;
    }
    if (!shippingMethodId) {
      toast.error("Select a shipping method.");
      setStep("shipping");
      return;
    }
    const gangSheetItems = items.filter((i) => i.requiresGangSheet && i.gangSheetLayoutId);
    if (gangSheetItems.length > 0 && !confirmedGangSheets) {
      toast.error("Please confirm your gang sheet previews before placing the order.");
      setStep("confirm");
      return;
    }

    setSubmitting(true);
    let createdCount = 0;
    let failedCount = 0;

    try {
      for (const item of items) {
        const line = totals.lines.find((l) => l.id === item.id);
        const lineTotalGbp = line?.net ?? 0;
        const lineTaxGbp = line?.tax ?? 0;
        const noteLines = [
          `Total: ${fmt(lineTotalGbp)}`,
          totals.taxEnabled ? `${totals.taxLabel}${totals.taxRateLabel}: ${fmt(lineTaxGbp)}` : null,
          `Shipping: ${selectedShipping?.name ?? "—"} (${fmt(totals.shipping)})`,
          items.length > 1
            ? `Preference: ${shipPreference === "complete" ? "Ship complete" : "Ship as ready"}`
            : null,
          item.variantLabel ? `Variant: ${item.variantLabel}` : null,
          item.notes ? `Item notes: ${item.notes}` : null,
          orderNotes ? `Order notes: ${orderNotes}` : null,
          item.uploadedArtworkUrl === "pending-upload"
            ? "Customer will upload own gang sheet."
            : null,
          item.gangSheetLayoutId ? `Gang sheet layout: ${item.gangSheetLayoutId}` : null,
          item.requiresGangSheet && item.gangSheetLayoutId && confirmedGangSheets
            ? `Gang sheet preview confirmed by customer at ${new Date().toISOString()}`
            : null,
        ].filter(Boolean);
        const gangSheetConfirmedAt =
          item.requiresGangSheet && item.gangSheetLayoutId && confirmedGangSheets
            ? new Date().toISOString()
            : null;
        // Reserve an order number — this is what marks the job as a placed order.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data: orderNum, error: orderNumErr } = await (supabase as any).rpc(
          "next_order_number",
        );
        if (orderNumErr) {
          console.error("Failed to reserve order number:", orderNumErr);
          failedCount++;
          continue;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data: insertedJob, error } = await (supabase as any)
          .from("jobs")
          .insert({
            customer_id: profile.customer_id,
            job_title: item.gangSheetName?.trim() || item.productName,
            product_type: item.productName,
            gang_sheet_name: item.gangSheetName?.trim() || null,
            quantity: item.quantity,
            metres: item.metres,
            status: "New",
            notes: noteLines.join("\n"),
            cutting_tier_id: item.cuttingTierId ?? null,
            gangsheet_design_id: item.gangSheetLayoutId ?? null,
            gangsheet_preview_url: item.gangSheetPreviewUrl ?? null,
            gangsheet_saved_at: item.gangSheetSavedAt ?? null,
            gangsheet_confirmed_at: gangSheetConfirmedAt,
            gangsheet_confirmed_by: gangSheetConfirmedAt ? (profile.id ?? null) : null,
            order_number: orderNum,
          })
          .select("id")
          .single();
        if (error) {
          console.error("Job insert failed:", error);
          failedCount++;
        } else {
          createdCount++;
          const newJobId = insertedJob?.id as string | undefined;
          if (newJobId) {
            const fileRows: Array<Record<string, unknown>> = [];
            // Customer-uploaded artwork (real URL only — skip legacy "pending-upload" sentinel)
            if (item.uploadedArtworkUrl && item.uploadedArtworkUrl !== "pending-upload") {
              fileRows.push({
                job_id: newJobId,
                file_url: item.uploadedArtworkUrl,
                file_name: item.uploadedArtworkName ?? "Customer artwork",
                file_type: "artwork",
              });
            }
            // Gang sheet preview (from Drip builder)
            if (item.gangSheetPreviewUrl) {
              fileRows.push({
                job_id: newJobId,
                file_url: item.gangSheetPreviewUrl,
                file_name: `Gang sheet ${item.gangSheetLayoutId ?? ""}.png`.trim(),
                file_type: "gang_sheet",
              });
            }
            if (fileRows.length > 0) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const { error: filesErr } = await (supabase as any)
                .from("job_files")
                .insert(fileRows);
              if (filesErr) {
                console.error("Job files insert failed:", filesErr);
              }
            }
            if (gangSheetConfirmedAt) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const { error: actErr } = await (supabase as any).from("job_activity").insert({
                job_id: newJobId,
                event_type: "gang_sheet_confirmed",
                message: `Customer confirmed gang sheet preview (design ${
                  item.gangSheetLayoutId ?? "—"
                }) at checkout`,
              });
              if (actErr) {
                console.error("Gang sheet confirmation activity insert failed:", actErr);
              }
            }
            // Notify all staff that a new order/job was placed
            try {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const { data: staffRows } = await (supabase as any)
                .from("profiles")
                .select("id")
                .in("role", ["staff", "admin"]);
              const staffIds: string[] = (staffRows ?? [])
                .map((r: { id: string }) => r.id)
                .filter((rid: string) => rid && rid !== profile.id);
              await Promise.all(
                staffIds.map((uid) =>
                  createNotification({
                    user_id: uid,
                    type: "job_created",
                    title: "New job placed",
                    message: `${profile.full_name || profile.email || "A customer"} placed "${item.gangSheetName?.trim() || item.productName}"`,
                    data: { job_id: newJobId },
                    priority: 1,
                  }).catch(() => null),
                ),
              );
            } catch (notifyErr) {
              console.error("Notify staff of new job failed:", notifyErr);
            }
          }
        }
      }

      if (createdCount > 0) {
        toast.success(`Created ${createdCount} job${createdCount === 1 ? "" : "s"}.`);
        clearCart();
        nav({ to: "/app/customer/jobs" });
      } else {
        toast.error("Could not create jobs. Please contact support.");
      }
      if (failedCount > 0) {
        toast.warning(`${failedCount} item${failedCount === 1 ? "" : "s"} could not be created.`);
      }
    } catch (e) {
      toast.error((e as Error).message ?? "Checkout failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitting) {
    return (
      <div className="space-y-6">
        <HummingbirdLoader
          title="Placing your order…"
          subtitle="Creating your jobs and confirming details"
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-12">
      <div className="flex items-center justify-between">
        <Link
          to="/app/customer/cart"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back to cart
        </Link>
      </div>

      <div>
        <h1 className="text-3xl font-bold tracking-tight">Checkout</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Review your order, choose shipping, and place your jobs.
        </p>
      </div>

      {/* Step indicator */}
      <ol className="flex items-center gap-2 text-xs font-medium">
        {(["review", "shipping", "confirm"] as Step[]).map((s, idx) => {
          const labels = { review: "Review", shipping: "Shipping", confirm: "Confirm" };
          const isActive = s === step;
          const isDone =
            (step === "shipping" && s === "review") ||
            (step === "confirm" && (s === "review" || s === "shipping"));
          return (
            <li key={s} className="flex items-center gap-2">
              <button
                onClick={() => setStep(s)}
                className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 transition-colors ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : isDone
                      ? "bg-primary/10 text-primary"
                      : "bg-muted text-muted-foreground"
                }`}
              >
                {isDone && <CheckCircle2 className="h-3 w-3" />}
                <span className="text-[10px] tabular-nums opacity-70">{idx + 1}</span>
                {labels[s]}
              </button>
              {idx < 2 && <span className="text-muted-foreground/40">·</span>}
            </li>
          );
        })}
      </ol>

      <div className="grid gap-6 lg:grid-cols-[1fr_360px] items-start">
        <div className="space-y-4">
          {/* Step: Review */}
          {step === "review" && (
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
              <div className="rounded-3xl border border-border bg-card p-6 shadow-soft space-y-4">
                <h2 className="text-base font-semibold">Review items</h2>
                <ul className="divide-y divide-border">
                  {items.map((item) => (
                    <li key={item.id} className="py-3 flex gap-3 items-start">
                      <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-muted">
                        {item.productImage && (
                          <img
                            src={item.productImage}
                            alt={item.productName}
                            className="h-full w-full object-cover"
                          />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{item.productName}</p>
                        <p className="text-xs text-muted-foreground">
                          {item.variantLabel ? `${item.variantLabel} · ` : ""}
                          {item.metres}m × {item.quantity}
                        </p>
                        {item.requiresGangSheet && !item.gangSheetLayoutId && (
                          <p className="mt-0.5 text-[11px] text-destructive inline-flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" /> Gang sheet not saved
                          </p>
                        )}
                      </div>
                      <span className="text-sm font-semibold tabular-nums">
                        {fmt(totals.lines.find((l) => l.id === item.id)?.net ?? 0)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {pendingGangSheetItems.length > 0 && (
                <div className="mt-4 rounded-2xl border border-destructive/40 bg-destructive/5 p-4 flex gap-3">
                  <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-destructive">
                      Cannot place order — {pendingGangSheetItems.length} item
                      {pendingGangSheetItems.length === 1 ? "" : "s"} missing a saved gang sheet
                    </p>
                    <p className="text-destructive/80 mt-0.5">
                      Open the gang sheet builder and save your design for each item before checking
                      out.
                    </p>
                    <Link to="/app/customer/cart">
                      <Button variant="outline" size="sm" className="mt-2 rounded-full">
                        Resolve in cart
                      </Button>
                    </Link>
                  </div>
                </div>
              )}

              <div className="mt-4 flex justify-end">
                <Button
                  onClick={() => setStep("shipping")}
                  disabled={pendingGangSheetItems.length > 0}
                  className="rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
                >
                  Continue to shipping
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step: Shipping */}
          {step === "shipping" && (
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
              <div className="rounded-3xl border border-border bg-card p-6 shadow-soft space-y-4">
                <h2 className="text-base font-semibold flex items-center gap-2">
                  <Truck className="h-4 w-4" /> Shipping
                </h2>

                <div className="space-y-3">
                  <Label className="text-sm">Shipping method</Label>
                  {activeShipping.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No shipping methods configured. Please contact support.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {activeShipping.map((m) => (
                        <ShippingOption
                          key={m.id}
                          method={m}
                          selected={shippingMethodId === m.id}
                          onSelect={() => setShippingMethodId(m.id)}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {items.length > 1 && (
                  <div className="border-t border-border pt-4">
                    <Label className="text-sm">Shipment preference</Label>
                    <div className="mt-2 grid gap-2 sm:grid-cols-2">
                      <button
                        type="button"
                        onClick={() => setShipPreference("complete")}
                        className={`rounded-2xl border p-3 text-left transition-all ${
                          shipPreference === "complete"
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-primary/40"
                        }`}
                      >
                        <p className="font-medium text-sm">Ship complete</p>
                        <p className="text-xs text-muted-foreground">
                          Wait for all jobs to be ready, ship together.
                        </p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setShipPreference("part")}
                        className={`rounded-2xl border p-3 text-left transition-all ${
                          shipPreference === "part"
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-primary/40"
                        }`}
                      >
                        <p className="font-medium text-sm">Ship as ready</p>
                        <p className="text-xs text-muted-foreground">
                          Send each job as soon as it's done.
                        </p>
                      </button>
                    </div>
                  </div>
                )}

                <div className="border-t border-border pt-4">
                  <Label className="text-sm" htmlFor="order-notes">
                    Order notes (optional)
                  </Label>
                  <textarea
                    id="order-notes"
                    value={orderNotes}
                    onChange={(e) => setOrderNotes(e.target.value)}
                    placeholder="Anything we should know about this order?"
                    className="mt-2 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm min-h-[80px]"
                  />
                </div>
              </div>

              <div className="mt-4 flex justify-between">
                <Button
                  variant="outline"
                  className="rounded-full"
                  onClick={() => setStep("review")}
                >
                  Back
                </Button>
                <Button
                  onClick={() => setStep("confirm")}
                  disabled={!shippingMethodId}
                  className="rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
                >
                  Continue
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step: Confirm */}
          {step === "confirm" && (
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
              <div className="rounded-3xl border border-border bg-card p-6 shadow-soft space-y-4">
                <h2 className="text-base font-semibold">Confirm and place order</h2>
                {(() => {
                  const gsItems = items.filter((i) => i.requiresGangSheet && i.gangSheetLayoutId);
                  if (gsItems.length === 0) return null;
                  return (
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Review your gang sheets</p>
                      <p className="text-xs text-muted-foreground">
                        Once placed, these designs go straight to production. Check each preview
                        carefully — what you see here is what will be printed.
                      </p>
                      <ul className="grid gap-3 sm:grid-cols-2">
                        {gsItems.map((item) => (
                          <li
                            key={item.id}
                            className="rounded-2xl border border-border bg-background p-3 flex gap-3"
                          >
                            <div className="h-24 w-24 shrink-0 overflow-hidden rounded-lg border border-border bg-muted">
                              {item.gangSheetPreviewUrl ? (
                                <img
                                  src={item.gangSheetPreviewUrl}
                                  alt={`Gang sheet for ${item.productName}`}
                                  className="h-full w-full object-contain"
                                  onError={(e) => {
                                    (e.currentTarget as HTMLImageElement).style.display = "none";
                                  }}
                                />
                              ) : (
                                <div className="grid h-full w-full place-items-center text-[10px] text-muted-foreground p-2 text-center">
                                  Preview not available
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0 text-xs space-y-1">
                              <p className="font-medium text-sm truncate">{item.productName}</p>
                              {item.variantLabel && (
                                <p className="text-muted-foreground">{item.variantLabel}</p>
                              )}
                              <p className="text-muted-foreground">
                                {item.metres}m × {item.quantity}
                              </p>
                              <p className="font-mono text-[10px] text-muted-foreground truncate">
                                #{item.gangSheetLayoutId}
                              </p>
                              <Link
                                to="/app/customer/cart"
                                className="text-primary hover:underline"
                              >
                                Edit in cart
                              </Link>
                            </div>
                          </li>
                        ))}
                      </ul>
                      <label className="flex items-start gap-2 rounded-2xl border border-border bg-muted/30 p-3 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={confirmedGangSheets}
                          onChange={(e) => setConfirmedGangSheets(e.target.checked)}
                          className="mt-0.5"
                        />
                        <span>
                          I've reviewed every gang sheet preview above and confirm they are ready to
                          print.
                        </span>
                      </label>
                    </div>
                  );
                })()}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Items</span>
                    <span>{items.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Shipping</span>
                    <span>{selectedShipping?.name ?? "—"}</span>
                  </div>
                  {items.length > 1 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Preference</span>
                      <span>
                        {shipPreference === "complete" ? "Ship complete" : "Ship as ready"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="rounded-2xl bg-muted/40 p-4 text-xs text-muted-foreground">
                  By placing this order, each cart item becomes a job in your FlightDeck. Our team
                  will confirm pricing and contact you if anything needs clarification.
                </div>
              </div>

              <div className="mt-4 flex justify-between">
                <Button
                  variant="outline"
                  className="rounded-full"
                  onClick={() => setStep("shipping")}
                >
                  Back
                </Button>
                <Button
                  onClick={handlePlaceOrder}
                  disabled={
                    submitting ||
                    pendingGangSheetItems.length > 0 ||
                    (items.some((i) => i.requiresGangSheet && i.gangSheetLayoutId) &&
                      !confirmedGangSheets)
                  }
                  className="rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90 gap-2"
                >
                  <Zap className="h-4 w-4" />
                  {submitting ? "Placing…" : "Place order"}
                </Button>
              </div>
            </motion.div>
          )}
        </div>

        {/* Summary sidebar */}
        <aside className="space-y-4 lg:sticky lg:top-6">
          <div className="rounded-3xl border border-border bg-card p-6 shadow-soft space-y-3">
            <h2 className="text-base font-semibold">Summary</h2>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="tabular-nums">{fmt(totals.subtotal)}</span>
              </div>
              {totals.taxEnabled && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {totals.taxLabel}
                    {totals.taxRateLabel}
                  </span>
                  <span className="tabular-nums">{fmt(totals.tax)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Shipping</span>
                <span className="tabular-nums">
                  {selectedShipping ? fmt(totals.shipping) : "—"}
                </span>
              </div>
            </div>
            <div className="border-t border-border pt-3 flex justify-between text-base font-semibold">
              <span>Total</span>
              <span className="tabular-nums">{fmt(totals.total)}</span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function ShippingOption({
  method,
  selected,
  onSelect,
}: {
  method: ShippingMethod;
  selected: boolean;
  onSelect: () => void;
}) {
  const fmt = useFormatMoney();
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full rounded-2xl border p-3 text-left transition-all flex items-center justify-between gap-3 ${
        selected ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
      }`}
    >
      <div>
        <p className="font-medium text-sm">{method.name}</p>
        {method.description && (
          <p className="text-xs text-muted-foreground">{method.description}</p>
        )}
      </div>
      <span className="text-sm font-semibold tabular-nums">{fmt(Number(method.price))}</span>
    </button>
  );
}
